// ============================================
// pages/transportation/TransportationDashboard.tsx — Real dashboard (client-side rollup)
// Reads live from transport_trips, transport_bills, vehicles, transport_slabs.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, Loader2, Navigation, DollarSign, AlertTriangle,
  ArrowUpRight, Layers, Wallet,
} from 'lucide-react';
import {
  seasonsAPI, transportTripsAPI, transportBillsAPI,
  vehiclesAPI, transportSlabsAPI, htContractorsAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

export const TransportationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [trips, setTrips] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [slabs, setSlabs] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, v, sl, c] = await Promise.all([
          seasonsAPI.list(),
          vehiclesAPI.list(),
          transportSlabsAPI.list(),
          htContractorsAPI.list(),
        ]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setVehicles(v.data);
        setSlabs(Array.isArray(sl.data) ? sl.data : []);
        setContractors(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [t, b] = await Promise.all([
          transportTripsAPI.list(selectedSeasonId),
          transportBillsAPI.list(selectedSeasonId),
        ]);
        setTrips(Array.isArray(t.data) ? t.data : []);
        setBills(Array.isArray(b.data) ? b.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load season data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const tripTotals = useMemo(() => ({
    total: trips.length,
    approved: trips.filter((t) => t.status === 'manager_approved').length,
    pending: trips.filter((t) => ['submitted', 'dm_verified'].includes(t.status)).length,
    total_bags: trips.reduce((s, t) => s + num(t.bags_sent), 0),
    total_weight: trips.reduce((s, t) => s + num(t.weight_qtl), 0),
    total_km: trips.reduce((s, t) => s + num(t.distance_claimed_km), 0),
    total_claimed: trips.reduce((s, t) => s + num(t.amount_claimed), 0),
  }), [trips]);

  const billTotals = useMemo(() => ({
    total: bills.length,
    approved: bills.filter((b) => b.status === 'manager_approved').length,
    pending: bills.filter((b) => ['submitted', 'dm_verified'].includes(b.status)).length,
    claimed: bills.reduce((s, b) => s + num(b.total_amount_claimed), 0),
    verified: bills.reduce((s, b) => s + num(b.total_amount_verified), 0),
    released: bills.reduce((s, b) => s + num(b.payment_released), 0),
    tds: bills.reduce((s, b) => s + num(b.tds_amount), 0),
    balance: bills.reduce((s, b) => s + num(b.balance_to_release), 0),
  }), [bills]);

  const vehicleStats = useMemo(() => {
    const now = new Date();
    const in30 = new Date(now);
    in30.setDate(in30.getDate() + 30);
    const expired = (d?: string) => !!d && new Date(d) < now;
    const expiring = (d?: string) => !!d && new Date(d) >= now && new Date(d) <= in30;
    return {
      total: vehicles.length,
      active: vehicles.filter((v) => v.status === 'active').length,
      maintenance: vehicles.filter((v) => v.status === 'maintenance').length,
      expired: vehicles.filter((v) =>
        expired(v.insurance_expiry) || expired(v.fitness_expiry) || expired(v.permit_expiry)
      ).length,
      expiring: vehicles.filter((v) =>
        expiring(v.insurance_expiry) || expiring(v.fitness_expiry) || expiring(v.permit_expiry)
      ).length,
    };
  }, [vehicles]);

  // Top 5 contractors by verified amount
  const topContractors = useMemo(() => {
    const contractorMap: Record<number, string> = {};
    contractors.forEach((c) => { contractorMap[c.id] = c.firm_name; });
    const agg: Record<number, { name: string; trips: number; verified: number; released: number }> = {};
    for (const b of bills) {
      const cid = b.contractor_id;
      if (!agg[cid]) {
        agg[cid] = { name: contractorMap[cid] || `Contractor ${cid}`, trips: 0, verified: 0, released: 0 };
      }
      agg[cid].trips += num(b.total_trips);
      agg[cid].verified += num(b.total_amount_verified);
      agg[cid].released += num(b.payment_released);
    }
    return Object.values(agg)
      .sort((a, b) => b.verified - a.verified)
      .slice(0, 5);
  }, [bills, contractors]);

  if (loading && trips.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Truck className="w-7 h-7 text-blue-600" />
          Transportation Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">Live rollup of trips, bills, vehicles, and transporter payments</p>
      </div>

      <div className="mb-4 max-w-xs">
        <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
        </select>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

      {/* Top stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div onClick={() => navigate('/transportation/trips')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Truck className="w-6 h-6 text-blue-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Total Trips</p>
          <p className="text-3xl font-bold text-gray-900">{tripTotals.total}</p>
          <p className="text-[10px] text-gray-400 mt-1">{tripTotals.approved} approved · {tripTotals.pending} pending</p>
        </div>
        <div onClick={() => navigate('/transportation/billing')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Layers className="w-6 h-6 text-indigo-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Transport Bills</p>
          <p className="text-3xl font-bold text-gray-900">{billTotals.total}</p>
          <p className="text-[10px] text-gray-400 mt-1">{formatAmount(billTotals.verified)} verified</p>
        </div>
        <div onClick={() => navigate('/transportation/vehicles')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Truck className={`w-6 h-6 ${vehicleStats.expired > 0 ? 'text-red-600' : 'text-green-600'}`} />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Vehicles</p>
          <p className="text-3xl font-bold text-gray-900">{vehicleStats.total}</p>
          <p className="text-[10px] text-gray-400 mt-1">
            {vehicleStats.active} active
            {vehicleStats.expired > 0 && <span className="text-red-600 ml-1">· {vehicleStats.expired} expired</span>}
            {vehicleStats.expiring > 0 && <span className="text-orange-600 ml-1">· {vehicleStats.expiring} expiring</span>}
          </p>
        </div>
        <div onClick={() => navigate('/transportation/payments')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Wallet className="w-6 h-6 text-green-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Pending Payments</p>
          <p className="text-3xl font-bold text-orange-700">{formatAmount(billTotals.balance)}</p>
          <p className="text-[10px] text-gray-400 mt-1">{formatAmount(billTotals.released)} released</p>
        </div>
      </div>

      {/* Trip/Bill flow + payment summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Navigation className="w-4 h-4" />Trip Statistics
          </h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-[10px] text-gray-500">Bags Sent</p>
              <p className="text-lg font-bold text-blue-700">{tripTotals.total_bags.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-[10px] text-gray-500">Weight (Qtl)</p>
              <p className="text-lg font-bold text-purple-700">{num(tripTotals.total_weight).toFixed(2)}</p>
            </div>
            <div className="p-3 bg-cyan-50 rounded-lg">
              <p className="text-[10px] text-gray-500">Total KM</p>
              <p className="text-lg font-bold text-cyan-700">{num(tripTotals.total_km).toFixed(1)}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-sm">
            <span className="text-gray-500">Total claimed:</span>
            <span className="font-bold">{formatAmount(tripTotals.total_claimed)}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />Payment Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Claimed:</span>
              <span className="font-semibold">{formatAmount(billTotals.claimed)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Verified:</span>
              <span className="font-semibold">{formatAmount(billTotals.verified)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">TDS Withheld:</span>
              <span className="font-semibold text-red-600">-{formatAmount(billTotals.tds)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Released:</span>
              <span className="font-semibold text-green-700">{formatAmount(billTotals.released)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-800">Balance:</span>
              <span className="font-bold text-orange-600">{formatAmount(billTotals.balance)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top contractors + expiring vehicles alert */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Top 5 Contractors (by verified amount)</h3>
          {topContractors.length === 0 ? (
            <p className="text-xs text-gray-400 py-3">No bills yet this season.</p>
          ) : (
            <div className="space-y-2">
              {topContractors.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                  <div>
                    <div className="font-medium text-gray-900">{c.name}</div>
                    <div className="text-[10px] text-gray-400">{c.trips} trips</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">{formatAmount(c.verified)}</div>
                    <div className="text-[10px] text-green-600">{formatAmount(c.released)} paid</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />Compliance Alerts
          </h3>
          {vehicleStats.expired === 0 && vehicleStats.expiring === 0 ? (
            <p className="text-xs text-gray-400 py-3">All vehicle compliance documents are current.</p>
          ) : (
            <div className="space-y-3">
              {vehicleStats.expired > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-sm text-red-700">{vehicleStats.expired} vehicle{vehicleStats.expired !== 1 ? 's' : ''} with expired documents</span>
                  <button onClick={() => navigate('/transportation/vehicles')}
                    className="text-xs font-semibold text-red-700 hover:underline">
                    View →
                  </button>
                </div>
              )}
              {vehicleStats.expiring > 0 && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <span className="text-sm text-orange-700">{vehicleStats.expiring} vehicle{vehicleStats.expiring !== 1 ? 's' : ''} expiring in 30 days</span>
                  <button onClick={() => navigate('/transportation/vehicles')}
                    className="text-xs font-semibold text-orange-700 hover:underline">
                    View →
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
            {slabs.length} rate slab{slabs.length !== 1 ? 's' : ''} configured ·{' '}
            <button onClick={() => navigate('/transportation/slabs')}
              className="text-blue-600 hover:underline">Manage →</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransportationDashboard;
